import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import { getToken, onTokenChange, setToken } from "./tokenStore";
import api from "./api";

// Uma conexão só para o app inteiro.
//
// Antes cada tela chamava `io()` por conta própria. O socket.io-client 4 não
// reaproveita o Manager quando o namespace já existe no cache — ele abre um
// novo — então 22 telas viravam 22 WebSockets, e cada `disconnect()` de tela
// que desmontava derrubava um handshake ainda em andamento. Daí a enxurrada de
// "WebSocket is closed before the connection is established" no console.
//
// Aqui a conexão é única e contada por referência: cada consumidor recebe um
// controle que só enxerga os próprios ouvintes. Ao desmontar, ele remove os
// que registrou; a conexão real só cai quando o último consumidor sai.

let socketReal = null;
let tokenDaConexao = null;
let consumidores = 0;

// Separa o endereço da API em origem + caminho do Socket.IO.
//
// Quando a API mora num subcaminho ("https://dominio/api/"), passar a URL
// inteira para o io() não funciona: o socket.io-client lê o caminho como
// *namespace*, não como path do transporte, e o servidor responde "Invalid
// namespace". O caminho tem que ir na opção `path`.
//
// Com a API na raiz ("http://localhost:8080/") o resultado é "/socket.io" —
// exatamente o padrão de antes.
export const enderecoDoSocket = () => {
	const base = String(getBackendUrl() || "");

	try {
		const url = new URL(base, window.location.origin);
		const prefixo = url.pathname.replace(/\/+$/, "");

		return { origem: url.origin, path: `${prefixo}/socket.io` };
	} catch {
		return { origem: base, path: "/socket.io" };
	}
};

const lerToken = () => getToken();

// Sem token não há o que ouvir: o backend recusa o handshake e o cliente fica
// tentando de novo. É o caso da tela de login.
const controleVazio = {
	on: () => controleVazio,
	off: () => controleVazio,
	emit: () => controleVazio,
	disconnect: () => {}
};

// Mantém o handshake da próxima reconexão com o token válido.
//
// O token só é verificado no handshake. Sem isto, uma reconexão depois dos 15
// minutos de validade — queda de rede, notebook que dormiu, servidor que
// reiniciou — repetia o token vencido, era recusada, e o cliente tentava de
// novo com o mesmo token para sempre: a tela parava de receber QR Code,
// mensagem nova e mudança de status, sem nenhum aviso.
const aplicarToken = (token) => {
	if (!socketReal || !token) return;

	tokenDaConexao = token;

	if (socketReal.io?.opts?.query) {
		socketReal.io.opts.query.token = token;
	}
	if (socketReal.auth) {
		socketReal.auth.token = token;
	}
};

// "resync": o aviso de que a tela precisa buscar tudo de novo.
//
// O tempo real só entrega o que acontece ENQUANTO a conexão está de pé. Se
// ela cai — notebook que dormiu, wi-fi que trocou, proxy que corta conexão
// ociosa —, o que aconteceu no intervalo não chega nunca: o socket volta,
// entra nas salas de novo e a tela continua mostrando o estado de antes da
// queda. É o "deixei aberto a noite toda e perdeu as informações; só voltou
// depois do F5".
//
// Um evento sintético, e não o `connect` puro, porque o `connect` também
// dispara na primeira conexão (e o wrapper o repete para quem monta depois):
// recarregar ali seria buscar duas vezes toda vez que uma tela abre.
const ouvintesDeResync = new Set();

const avisarQueDessincronizou = () => {
  ouvintesDeResync.forEach(fn => {
    try {
      fn();
    } catch {
      // Uma tela que falha ao se ressincronizar não pode impedir as outras.
    }
  });
};

// Se o tempo real está de pé, para a faixa de "Reconectando…" do layout.
// Sem ela a tela parada parece tela atualizada.
let caido = false;
const ouvintesDoEstado = new Set();

const mudarEstado = (agoraCaido) => {
  if (caido === agoraCaido) return;
  caido = agoraCaido;
  ouvintesDoEstado.forEach(fn => fn(caido));
};

// Devolve a função que para de ouvir. Chama já com o estado atual.
export const ouvirQuedaDoTempoReal = (fn) => {
  ouvintesDoEstado.add(fn);
  fn(caido);
  return () => ouvintesDoEstado.delete(fn);
};

let jaConectouUmaVez = false;
let escondidaDesde = null;

// Voltar para a aba depois de um tempo longe vale o mesmo que reconectar: o
// navegador congela temporizador e socket em aba de fundo, e a pessoa volta
// para uma tela parada no tempo.
const MINIMO_ESCONDIDA_MS = 60 * 1000;

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      escondidaDesde = Date.now();
      return;
    }

    const tempo = escondidaDesde ? Date.now() - escondidaDesde : 0;
    escondidaDesde = null;

    if (tempo >= MINIMO_ESCONDIDA_MS) avisarQueDessincronizou();
  });

  window.addEventListener("online", avisarQueDessincronizou);
}

// Teto de tentativas para o caso de o servidor desligar por outro motivo que
// não o token (falta de permissão, por exemplo): sem ele, renovar e reconectar
// viraria laço quente. Zera a cada conexão que dá certo.
// ponytail: teto fixo de 5; se aparecer motivo novo de desligamento, vale
// distinguir pelo erro em vez de contar tentativas.
const MAXIMO_DE_VOLTAS = 5;
// Quanto a conexão precisa durar para valer como conexão que deu certo.
const SOBREVIVEU_MS = 5000;
let tentativasDeVolta = 0;
let cronometroDeVida = null;
let renovando = false;

const renovarTokenEVoltar = () => {
	if (renovando || tentativasDeVolta >= MAXIMO_DE_VOLTAS) return;

	renovando = true;
	tentativasDeVolta += 1;

	api
		.post("/auth/refresh_token")
		.then(({ data }) => {
			if (!data?.token || !socketReal) return;

			setToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			socketReal.connect();
		})
		.catch(() => {
			// O refresh token também venceu. Quem derruba a sessão é o
			// interceptor do useAuth, na próxima requisição — aqui só paramos.
			tentativasDeVolta = MAXIMO_DE_VOLTAS;
		})
		.finally(() => {
			renovando = false;
		});
};

function connectToSocket() {
	const token = lerToken();

	if (!token) return controleVazio;

	// Token diferente com ninguém pendurado significa outra sessão: descarta a
	// conexão antiga. Com consumidores ativos não se mexe — o backend só valida
	// o token no handshake, e derrubar a conexão aqui deixaria a lista de
	// tickets e as notificações mudas até a tela remontar. A renovação de token
	// acontece a cada poucos minutos; ela não pode custar a conexão de todos.
	if (socketReal && tokenDaConexao !== token && consumidores <= 0) {
		socketReal.disconnect();
		socketReal = null;
	}

	// Conexão viva com token renovado: só alinha o que vai no próximo
	// handshake, sem derrubar a atual. (O caminho normal é o ouvinte do
	// tokenStore lá embaixo; isto cobre quem monta entre uma renovação e a
	// outra.)
	if (socketReal && tokenDaConexao !== token) {
		aplicarToken(token);
	}

	if (!socketReal) {
		tokenDaConexao = token;
		const { origem, path } = enderecoDoSocket();

		socketReal = openSocket(origem, {
			path,
			transports: ["websocket", "polling"],
			query: { token }
		});

		socketReal.on("connect", () => {
			// Primeira conexão é só conexão; da segunda em diante houve queda,
			// e o que passou no intervalo não voltou.
			//
			// Menos quando o servidor recuperou a conexão
			// (`connectionStateRecovery` em `libs/socket.ts`): aí ele já
			// reenviou o que passou, e buscar tudo de novo seria só custo.
			if (jaConectouUmaVez && !socketReal.recovered) avisarQueDessincronizou();

			mudarEstado(false);

			jaConectouUmaVez = true;

			// Zerar o teto aqui, na hora, era um laço.
			//
			// O backend não recusa só no handshake: ele também desliga DEPOIS
			// do connect, já dentro do `io.on("connection")` — é o caso do
			// usuário desativado (`libs/socket.ts`). Nesse caminho o `connect`
			// chega, zerava o contador, vinha o desligamento, renovávamos o
			// token e voltávamos, para sempre. Medido: 225 POST
			// /auth/refresh_token seguidos, até o backend responder 429.
			//
			// O contador só zera quando a conexão SOBREVIVE: conexão que morre
			// em seguida não conta como conexão que deu certo.
			clearTimeout(cronometroDeVida);
			cronometroDeVida = setTimeout(() => {
				tentativasDeVolta = 0;
			}, SOBREVIVEU_MS);
		});

		// Recusa do servidor NÃO reconecta sozinha — e era isto que matava a
		// tela depois de um tempo parada.
		//
		// O token vale 15 minutos e só é renovado quando alguma requisição HTTP
		// leva 403: ficar parado não renova nada. Quando a conexão cai por
		// qualquer motivo (proxy que corta conexão ociosa, wi-fi, notebook que
		// dormiu), o socket.io tenta de novo com o token VENCIDO, e o servidor
		// responde `socket.disconnect()` dentro do `io.on("connection")`. Esse
		// desligamento chega ao cliente como "io server disconnect", e o
		// socket.io-client trata como definitivo: `destroy()` e
		// `socket.active === false` — nenhuma retentativa, para sempre. Nada no
		// cliente observava isso, então a tela ficava muda sem um erro sequer, e
		// só o F5 resolvia (ele troca o cookie `jrt` por um token novo).
		//
		// Aqui a queda deixa de ser definitiva: renova o token pelo mesmo
		// caminho do interceptor e reconecta. O `setToken` avisa o tokenStore,
		// que é quem põe o token novo no próximo handshake.
		socketReal.on("disconnect", motivo => {
			// Caiu antes de sobreviver: a tentativa continua valendo.
			clearTimeout(cronometroDeVida);
			mudarEstado(true);

			// Os outros motivos o próprio socket.io reconecta.
			if (motivo !== "io server disconnect") return;

			renovarTokenEVoltar();
		});

		// Recusa do middleware de autenticação do backend (`io.use`), que diz
		// o motivo em vez de desligar calado. O socket.io-client destrói o
		// socket aqui também (`destroy()` no pacote CONNECT_ERROR,
		// build/cjs/socket.js:522-523), então sem isto a conexão morreria do
		// mesmo jeito — só que com nome.
		socketReal.on("connect_error", erro => {
			mudarEstado(true);

			if (String(erro?.message) !== "ERR_SESSION_EXPIRED") return;

			renovarTokenEVoltar();
		});
	}

	consumidores += 1;

	const meus = [];
	let ativo = true;

	const controle = {
		on(evento, handler) {
			if (!ativo) return controle;

			// "resync" é do wrapper, não do servidor: ninguém emite isso do
			// outro lado.
			if (evento === "resync") {
				meus.push([evento, handler]);
				ouvintesDeResync.add(handler);
				return controle;
			}

			meus.push([evento, handler]);
			socketReal.on(evento, handler);

			// As telas entram nas salas do Socket.IO dentro do "connect"
			// (`socket.on("connect", () => socket.emit("joinChatBox", id))`).
			// Quando cada tela abria a própria conexão isso sempre acontecia;
			// com a conexão compartilhada, quem chega depois encontra o socket
			// já conectado e o evento nunca mais dispara — a tela não entra na
			// sala e para de receber atualização em tempo real. Dispara na
			// hora, num tick à parte para o handler não rodar antes de o
			// componente terminar de montar.
			if (evento === "connect" && socketReal.connected) {
				setTimeout(() => {
					if (ativo) handler();
				}, 0);
			}

			return controle;
		},
		off(evento, handler) {
			const i = meus.findIndex(([e, h]) => e === evento && h === handler);
			if (i >= 0) meus.splice(i, 1);

			if (evento === "resync") {
				ouvintesDeResync.delete(handler);
				return controle;
			}

			if (socketReal) socketReal.off(evento, handler);
			return controle;
		},
		emit(...args) {
			if (ativo && socketReal) socketReal.emit(...args);
			return controle;
		},
		// Mantém o nome que as telas já usam. Não derruba a conexão dos outros:
		// só larga os ouvintes deste consumidor.
		disconnect() {
			if (!ativo) return;
			ativo = false;

			meus.forEach(([evento, handler]) => {
				if (evento === "resync") {
					ouvintesDeResync.delete(handler);
					return;
				}

				if (socketReal) socketReal.off(evento, handler);
			});
			meus.length = 0;

			consumidores -= 1;

			if (consumidores <= 0 && socketReal) {
				socketReal.disconnect();
				socketReal = null;
				tokenDaConexao = null;
				consumidores = 0;
				// Desligada de propósito (logout), não caída.
				mudarEstado(false);
			}
		}
	};

	return controle;
}

// O tokenStore avisa sozinho quando o token muda (login, renovação, logout).
onTokenChange(aplicarToken);

export default connectToSocket;
